import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import themeFrameMappingApi from '@services/themeFrameMappingApi.jsx';
import themeFilterMappingApi from '@services/themeFilterMappingApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';
import ThemeFrameSelectionModal from './ThemeFrameSelectionModal.jsx';
import ThemeFilterSelectionModal from './ThemeFilterSelectionModal.jsx';
import '@assets/js/common/table';
import '@assets/js/common/modal';
import '@assets/css/common/style_modal.css';

const PhotoSelectionModeSetting = ({ themeId, themePageMaster }) => {
  const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;

  // 상태 관리
  const [photoSelectionPage, setPhotoSelectionPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    messageText: '사진을 선택해주세요',
    messageColor: '#FFFFFF',
    instructionText: '원하는 사진을 선택하고 필터와 프레임을 골라주세요',
    instructionColor: '#FFFFFF',
    selectionIconColor: '#00FF00',
    selectionBorderColor: '#FFFFFF',
    selectionBorderWidth: 3,
    qrGuideText: 'QR코드를 인식해주세요',
    qrGuideColor: '#FFFFFF',
    qrDetailText: '*사진 원본 및 동영상 전송 개인정보 및 사진 수집 동의',
    qrDetailColor: '#FFFFFF',
    filterArrowColor: '#FFFFFF',
    frameArrowColor: '#FFFFFF',
    maxPhotoSelection: 4,
    minPhotoSelection: 1,
    filterSelectionEnabled: true,
    frameSelectionEnabled: true,
    qrCodeEnabled: true,
    videoSelectionEnabled: true,
    timerSeconds: 60,
    timerTimeoutRedirectUrl: 'start'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 프레임 & 필터 매핑 데이터 상태
  const [frameMappings, setFrameMappings] = useState([]);
  const [filterMappings, setFilterMappings] = useState([]);
  const [availableFrames, setAvailableFrames] = useState([]);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [frameFilter, setFrameFilter] = useState("all");
  const [filterFilter, setFilterFilter] = useState("all");

  // 전체 선택 상태
  const [selectAllFrames, setSelectAllFrames] = useState(false);
  const [selectAllFilters, setSelectAllFilters] = useState(false);

  // 모달 상태 관리
  const [showFrameModal, setShowFrameModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // 사진 선택 페이지 데이터 가져오기
        const pageData = await designApi.getPhotoSelectionPage(themeId);
        setPhotoSelectionPage({
          ...photoSelectionPage,
          ...pageData
        });

        console.log("사진 선택 페이지 데이터 로드 성공:", pageData);
      } catch (err) {
        console.error("사진 선택 페이지 데이터 로드 실패:", err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFrameMappings = async () => {
      try {
        const mappings = await themeFrameMappingApi.getThemeFrameMappings(themeId);
        setFrameMappings(mappings || []);
        console.log("프레임 매핑 데이터 로드 성공:", mappings);
      } catch (err) {
        console.error("프레임 매핑 데이터 로드 실패:", err);
      }
    };

    const fetchFilterMappings = async () => {
      try {
        const mappings = await themeFilterMappingApi.getThemeFilterMappings(themeId);
        setFilterMappings(mappings || []);
        console.log("필터 매핑 데이터 로드 성공:", mappings);
      } catch (err) {
        console.error("필터 매핑 데이터 로드 실패:", err);
      }
    };

    fetchData();
    fetchFrameMappings();
    fetchFilterMappings();
  }, [themeId]);

  // availableFrames 상태 변화 감지
  useEffect(() => {
    console.log('PhotoSelectionModeSetting - availableFrames 상태 변화:', {
      availableFrames,
      length: availableFrames.length,
      showFrameModal
    });
  }, [availableFrames, showFrameModal]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setPhotoSelectionPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setPhotoSelectionPage({
      ...photoSelectionPage,
      [resourceType]: resourceId
    });
  };

  // 선택 수량 조절 핸들러
  const handleSelectionCountChange = (type, value) => {
    const numValue = Math.max(1, Math.min(10, parseInt(value) || 1));
    
    if (type === 'max') {
      setPhotoSelectionPage(prev => ({
        ...prev,
        maxPhotoSelection: Math.max(numValue, prev.minPhotoSelection || 1)
      }));
    } else if (type === 'min') {
      setPhotoSelectionPage(prev => ({
        ...prev,
        minPhotoSelection: Math.min(numValue, prev.maxPhotoSelection || 10)
      }));
    }
  };

  // 테두리 두께 조절 핸들러
  const handleBorderWidthChange = (value) => {
    const width = Math.max(1, Math.min(10, parseInt(value) || 3));
    setPhotoSelectionPage({
      ...photoSelectionPage,
      selectionBorderWidth: width
    });
  };

  // 타이머 조절 핸들러
  const handleTimerDecrease = () => {
    setPhotoSelectionPage(prev => ({
      ...prev,
      timerSeconds: Math.max((prev.timerSeconds || 60) - 5, 5)
    }));
  };

  const handleTimerIncrease = () => {
    setPhotoSelectionPage(prev => ({
      ...prev,
      timerSeconds: (prev.timerSeconds || 60) + 5
    }));
  };

  // 체크박스 선택 핸들러
  const handleCheckboxChange = (type, id) => {
    if (type === 'frame') {
      setFrameMappings(prevMappings =>
        prevMappings.map(mapping =>
          (mapping.id || mapping.mappingId) === id ? { ...mapping, selected: !mapping.selected } : mapping
        )
      );
    } else if (type === 'filter') {
      setFilterMappings(prevMappings =>
        prevMappings.map(mapping =>
          (mapping.id || mapping.mappingId) === id ? { ...mapping, selected: !mapping.selected } : mapping
        )
      );
    }
  };

  // 전체 선택 핸들러
  const handleSelectAll = (type) => {
    if (type === 'frame') {
      setSelectAllFrames(!selectAllFrames);
      setFrameMappings(prevMappings =>
        prevMappings.map(mapping => ({ ...mapping, selected: !selectAllFrames }))
      );
    } else if (type === 'filter') {
      setSelectAllFilters(!selectAllFilters);
      setFilterMappings(prevMappings =>
        prevMappings.map(mapping => ({ ...mapping, selected: !selectAllFilters }))
      );
    }
  };

  // 추가 버튼 클릭 핸들러
  const handleAddClick = async (type) => {
    console.log('handleAddClick 호출됨:', type);
    setModalType(type);
    setIsEditMode(false);
    setEditTarget(null);
    
    if (type === 'frame') {
      try {
        console.log('프레임 추가 모달 열기 시작...');
        const frames = await themeFrameMappingApi.getAvailableFrames(themeId);
        console.log('사용 가능한 프레임 데이터:', frames);
        setAvailableFrames(frames || []);
        setShowFrameModal(true);
        console.log('프레임 모달 상태 설정 완료');
      } catch (error) {
        console.error("사용 가능한 프레임 조회 실패:", error);
        alert('프레임 목록을 불러오는데 실패했습니다.');
      }
    } else {
      try {
        const filters = await themeFilterMappingApi.getAvailableFilters(themeId);
        setAvailableFilters(filters || []);
        setShowFilterModal(true);
      } catch (error) {
        console.error("사용 가능한 필터 조회 실패:", error);
      }
    }
    
    document.body.classList.add("modal-open");
  };

  // 수정 버튼 클릭 핸들러
  const handleEditClick = (type, item) => {
    setModalType(type);
    setIsEditMode(true);
    setEditTarget(item);
    
    if (type === 'frame') {
      setShowFrameModal(true);
    } else {
      setShowFilterModal(true);
    }
    
    document.body.classList.add("modal-open");
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = (type, item) => {
    setModalType(type);
    setDeleteTarget(item);
    setShowDeleteModal(true);
    document.body.classList.add("modal-open");
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setShowFrameModal(false);
    setShowFilterModal(false);
    setShowDeleteModal(false);
    setIsEditMode(false);
    setEditTarget(null);
    setDeleteTarget(null);
    document.body.classList.remove("modal-open");
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        if (modalType === 'frame') {
          // ThemeFrameMappingResponse의 id 필드 사용
          await themeFrameMappingApi.removeFrameFromTheme(deleteTarget.id || deleteTarget.mappingId);
          // 데이터 다시 로드
          const mappings = await themeFrameMappingApi.getThemeFrameMappings(themeId);
          setFrameMappings(mappings || []);
        } else {
          // ThemeFilterMappingResponse의 id 필드 사용
          await themeFilterMappingApi.removeFilterFromTheme(deleteTarget.id || deleteTarget.mappingId);
          // 데이터 다시 로드
          const mappings = await themeFilterMappingApi.getThemeFilterMappings(themeId);
          setFilterMappings(mappings || []);
        }
        
        alert(`${modalType === 'frame' ? '프레임' : '필터'}이(가) 성공적으로 삭제되었습니다.`);
        handleCloseModal();
      } catch (error) {
        console.error('삭제 실패:', error);
        alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
      }
    }
  };

  // 저장 핸들러
  const handleSave = async (type, result) => {
    try {
      if (type === 'frame') {
        // 프레임 매핑 목록 새로고침
        const mappings = await themeFrameMappingApi.getThemeFrameMappings(themeId);
        setFrameMappings(mappings || []);
        
        const frames = await themeFrameMappingApi.getAvailableFrames(themeId);
        setAvailableFrames(frames || []);
      } else {
        // 필터 매핑 목록 새로고침
        const mappings = await themeFilterMappingApi.getThemeFilterMappings(themeId);
        setFilterMappings(mappings || []);
        
        const filters = await themeFilterMappingApi.getAvailableFilters(themeId);
        setAvailableFilters(filters || []);
      }
      
      alert(`${type === 'frame' ? '프레임' : '필터'}이(가) 성공적으로 추가/수정되었습니다.`);
    } catch (error) {
      console.error('데이터 새로고침 실패:', error);
    }
  };

  // 상세정보 모달 핸들러
  const handleInfoClick = (type, item) => {
    const displayItem = type === 'frame' ? {
      ...item,
      name: item.frameName || item.name,
      type: item.frameType || item.type,
      direction: item.orientation || item.direction
    } : {
      ...item,
      name: item.filterName || item.name,
      type: '필터'
    };
    
    setCurrentItem(displayItem);
    setModalType(type);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setCurrentItem(null);
  };

  // 저장 이벤트 리스너
  useEffect(() => {
    const eventName = `savePageDesign_select_mode`;

    const saveHandler = async (event) => {
      try {
        setIsLoading(true);
        const dataToSave = {
          ...photoSelectionPage,
          themeId: event.detail.themeId
        };
        const isSucceed = await designApi.savePhotoSelectionPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          alert("저장되었습니다.");
        } else {
          alert("저장에 실패했습니다.");
        }
      } catch (error) {
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    document.addEventListener(eventName, saveHandler);
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [photoSelectionPage, themeId]);

  // 테이블 초기화
  useEffect(() => {
    if (window.tableManager) {
      window.tableManager.initTable({
        tableSelector: '.common-table',
        filterSelector: '#frame-filter, #photo-filter-filter',
        columnWidthOptions: {
          equalWidth: false,
          firstColumnWidth: 5,
          lastColumnWidth: 5,
          fixedColumns: {
            1: 10,  // NO 열
            2: 20,  // 이름 열
            3: 15,  // 타입 열
            4: 10,  // 방향 열
            5: 10,  // 상세정보 열
            6: 10,  // 수정 열
            7: 10   // 삭제 열
          }
        }
      });
    }
  }, []);

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={photoSelectionPage.languageCode || "ko"}
            onChange={(e) => handleInputChange('languageCode', e.target.value)}
          >
            <option key="ko" value="ko">한국어</option>
            <option key="en" value="en">영어</option>
            <option key="jp" value="jp">일본어</option>
            <option key="cn" value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={photoSelectionPage.backgroundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundResourceId", id)}
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={photoSelectionPage.backgroundMusicResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundMusicResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>

      {/* QR 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">QR 설정</h4>
        <ResourceUploadField
          label="QR 체크 이미지"
          resourceId={photoSelectionPage.qrCheckIconResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("qrCheckIconResourceId", id)}
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">QR 안내 메시지</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="QR코드를 인식해주세요" 
              className="text-input"
              value={photoSelectionPage.qrGuideText || ""}
              onChange={(e) => handleInputChange('qrGuideText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">메시지 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.qrGuideColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.qrGuideColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('qrGuideColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">QR 상세 메시지</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="*사진 원본 및 동영상 전송 개인정보 및 사진 수집 동의" 
              className="text-input"
              value={photoSelectionPage.qrDetailText || ""}
              onChange={(e) => handleInputChange('qrDetailText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">상세 메시지 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.qrDetailColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.qrDetailColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('qrDetailColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 사진출력 버튼 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">사진출력 버튼 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={photoSelectionPage.printButtonResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("printButtonResourceId", id)}
          acceptTypes="image/*"
        />
        <ResourceUploadField
          label="동영상 출력 버튼"
          resourceId={photoSelectionPage.videoButtonResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("videoButtonResourceId", id)}
          acceptTypes="image/*"
        />
      </div>

      {/* 문구 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">문구 (사진선택/필터 선택/프레임 선택/QR코드 사용)</h4>
        <div className="settings-row">
          <div className="settings-label">메인 메시지</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="사진을 선택해주세요" 
              className="text-input"
              value={photoSelectionPage.messageText || ""}
              onChange={(e) => handleInputChange('messageText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">글자 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.messageColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.messageColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('messageColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">상세 안내</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="원하는 사진을 선택하고 필터와 프레임을 골라주세요" 
              className="text-input"
              value={photoSelectionPage.instructionText || ""}
              onChange={(e) => handleInputChange('instructionText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">상세 안내 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.instructionColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.instructionColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('instructionColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 사진 & 동영상 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">사진 & 동영상 선택</h4>
        <ResourceUploadField
          label="선택 아이콘"
          resourceId={photoSelectionPage.selectionIconResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("selectionIconResourceId", id)}
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">선택 아이콘 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.selectionIconColor || "#00FF00"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.selectionIconColor || "#00FF00"}
              onChange={(e) => handleInputChange('selectionIconColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">선택 테두리 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.selectionBorderColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.selectionBorderColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('selectionBorderColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">테두리 두께</div>
          <div className="width-control-container">
            <button 
              className="width-btn decrease-btn"
              onClick={() => handleBorderWidthChange((photoSelectionPage.selectionBorderWidth || 3) - 1)}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="number" 
              className="width-input" 
              min="1"
              max="10"
              value={photoSelectionPage.selectionBorderWidth || 3}
              onChange={(e) => handleBorderWidthChange(e.target.value)}
            />
            <button 
              className="width-btn increase-btn"
              onClick={() => handleBorderWidthChange((photoSelectionPage.selectionBorderWidth || 3) + 1)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">px</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">최소 선택 수량</div>
          <div className="count-control-container">
            <button 
              className="count-btn decrease-btn"
              onClick={() => handleSelectionCountChange('min', (photoSelectionPage.minPhotoSelection || 1) - 1)}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="number" 
              className="count-input" 
              min="1"
              max="10"
              value={photoSelectionPage.minPhotoSelection || 1}
              onChange={(e) => handleSelectionCountChange('min', e.target.value)}
            />
            <button 
              className="count-btn increase-btn"
              onClick={() => handleSelectionCountChange('min', (photoSelectionPage.minPhotoSelection || 1) + 1)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">장</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">최대 선택 수량</div>
          <div className="count-control-container">
            <button 
              className="count-btn decrease-btn"
              onClick={() => handleSelectionCountChange('max', (photoSelectionPage.maxPhotoSelection || 4) - 1)}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="number" 
              className="count-input" 
              min="1"
              max="10"
              value={photoSelectionPage.maxPhotoSelection || 4}
              onChange={(e) => handleSelectionCountChange('max', e.target.value)}
            />
            <button 
              className="count-btn increase-btn"
              onClick={() => handleSelectionCountChange('max', (photoSelectionPage.maxPhotoSelection || 4) + 1)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">장</span>
          </div>
        </div>
      </div>

      {/* 필터 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">필터 선택</h4>
        <ResourceUploadField
          label="화살표 이미지 (좌)"
          resourceId={photoSelectionPage.filterLeftArrowResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("filterLeftArrowResourceId", id)}
          acceptTypes="image/*"
        />
        <ResourceUploadField
          label="화살표 이미지 (우)"
          resourceId={photoSelectionPage.filterRightArrowResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("filterRightArrowResourceId", id)}
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">선택 테두리 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.filterArrowColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.filterArrowColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('filterArrowColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 프레임 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">프레임 선택</h4>
        <ResourceUploadField
          label="화살표 이미지 (좌)"
          resourceId={photoSelectionPage.frameLeftArrowResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("frameLeftArrowResourceId", id)}
          acceptTypes="image/*"
        />
        <ResourceUploadField
          label="화살표 이미지 (우)"
          resourceId={photoSelectionPage.frameRightArrowResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("frameRightArrowResourceId", id)}
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">선택 테두리 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={photoSelectionPage.frameArrowColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={photoSelectionPage.frameArrowColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('frameArrowColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 타이머 설정 섹션 */}
      {/* <div className="settings-box">
        <h4 className="settings-title">타이머 설정</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={handleTimerDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={photoSelectionPage.timerSeconds || 60}
              readOnly
            />
            <button 
              className="timer-btn"
              onClick={handleTimerIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타임 오버 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={photoSelectionPage.timerTimeoutRedirectUrl || "start"}
              onChange={(e) => handleInputChange('timerTimeoutRedirectUrl', e.target.value)}
            >
              <option key="start" value="start">시작화면</option>
              <option key="capture" value="capture">촬영화면</option>
              <option key="amount" value="amount">수량선택</option>
              <option key="payment" value="payment">결제화면</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div> */}

      {/* 효과음 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="사진 선택 효과음"
          resourceId={photoSelectionPage.photoSelectSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("photoSelectSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="필터 변경 효과음"
          resourceId={photoSelectionPage.filterChangeSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("filterChangeSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="프레임 변경 효과음"
          resourceId={photoSelectionPage.frameChangeSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("frameChangeSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="버튼 클릭 효과음"
          resourceId={photoSelectionPage.buttonClickSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("buttonClickSoundResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>

      {/* 프레임 데이터 섹션 헤더 */}
      <div className="data-section-header">
        <div className="data-section-title">
          <h4 className="section-title">프레임</h4>
          <div className="section-title-icon">
            <a
              href="#"
              className="angle-add-btn"
              title="프레임 추가"
              onClick={(e) => {
                e.preventDefault();
                handleAddClick('frame');
              }}
            >
              <i className="ri-add-circle-line"></i>
            </a>
          </div>
        </div>
        <div className="filter-dropdown-container">
          <div className="filter-dropdown">
            <select
              id="frame-filter"
              value={frameFilter}
              onChange={(e) => setFrameFilter(e.target.value)}
            >
              <option key="all" value="all">전체</option>
              <option key="1cut_hor&2cut_ver" value="1cut_hor&2cut_ver">1컷 가로 & 2컷 세로</option>
              <option key="1cut_ver&2cut_hor" value="1cut_ver&2cut_hor">1컷 세로 & 2컷 가로</option>
              <option key="3cut_ver&4cut_ver" value="3cut_ver&4cut_ver">3컷 세로 & 4컷 세로</option>
              <option key="3cut_hor&4cut_hor" value="3cut_hor&4cut_hor">3컷 가로 & 4컷 가로</option>
            </select>
          </div>
        </div>
      </div>

      {/* 프레임 테이블 */}
      <div className="common-table-container">
        <table className="common-table">
          <thead>
            <tr>
              <th>
                <div className="checkbox-container">
                  <input 
                    type="checkbox" 
                    id="select-all-frames"
                    checked={selectAllFrames}
                    onChange={() => handleSelectAll('frame')}
                  />
                  <label htmlFor="select-all-frames" className="custom-checkbox"></label>
                </div>
              </th>
              <th>NO</th>
              <th>프레임명</th>
              <th>타입</th>
              <th>방향</th>
              <th>상세정보</th>
              <th>수정</th>
              <th>삭제</th>
            </tr>
          </thead>
          <tbody>
            {frameMappings.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                  테마에 등록된 프레임이 없습니다.
                </td>
              </tr>
            ) : (
              frameMappings.map((mapping, index) => (
                <tr key={(mapping.id || mapping.mappingId) ? `frame-mapping-${mapping.id || mapping.mappingId}` : `frame-temp-${index}-${mapping.photoFrameId || 'new'}`}>
                  <td>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id={`frame-${mapping.id || mapping.mappingId || `temp-${index}`}`}
                        checked={mapping.selected || false}
                        onChange={() => handleCheckboxChange('frame', mapping.id || mapping.mappingId)}
                      />
                      <label htmlFor={`frame-${mapping.id || mapping.mappingId || `temp-${index}`}`} className="custom-checkbox"></label>
                    </div>
                  </td>
                  <td>{mapping.displayOrder || index + 1}</td>
                  <td>{mapping.frameName || '-'}</td>
                  <td>{mapping.frameType || '-'}</td>
                  <td>{mapping.orientation || '-'}</td>
                  <td><button className="info-btn" onClick={() => handleInfoClick('frame', mapping)}><i className="ri-information-line"></i></button></td>
                  <td><button className="edit-btn" onClick={() => handleEditClick('frame', mapping)}><i className="ri-pencil-line"></i></button></td>
                  <td><button className="delete-btn" onClick={() => handleDeleteClick('frame', mapping)}><i className="ri-close-line"></i></button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 필터 데이터 섹션 헤더 */}
      <div className="data-section-header">
        <div className="data-section-title">
          <h4 className="section-title">필터</h4>
          <div className="section-title-icon">
            <a
              href="#"
              className="filter-add-btn"
              title="필터 추가"
              onClick={(e) => {
                e.preventDefault();
                handleAddClick('filter');
              }}
            >
              <i className="ri-add-circle-line"></i>
            </a>
          </div>
        </div>
        <div className="filter-dropdown-container">
          <div className="filter-dropdown">
            <select
              id="photo-filter-filter"
              value={filterFilter}
              onChange={(e) => setFilterFilter(e.target.value)}
            >
              <option key="all" value="all">전체</option>
              <option key="photo-filter" value="photo-filter">필터</option>
            </select>
          </div>
        </div>
      </div>

      {/* 필터 테이블 */}
      <div className="common-table-container">
        <table className="common-table">
          <thead>
            <tr>
              <th>
                <div className="checkbox-container">
                  <input 
                    type="checkbox" 
                    id="select-all-filters"
                    checked={selectAllFilters}
                    onChange={() => handleSelectAll('filter')}
                  />
                  <label htmlFor="select-all-filters" className="custom-checkbox"></label>
                </div>
              </th>
              <th>NO</th>
              <th>필터명</th>
              <th>타입</th>
              <th>상세정보</th>
              <th>수정</th>
              <th>삭제</th>
            </tr>
          </thead>
          <tbody>
            {filterMappings.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  테마에 등록된 필터가 없습니다.
                </td>
              </tr>
            ) : (
              filterMappings.map((mapping, index) => (
                <tr key={(mapping.id || mapping.mappingId) ? `filter-mapping-${mapping.id || mapping.mappingId}` : `filter-temp-${index}-${mapping.photoFilterId || 'new'}`}>
                  <td>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id={`filter-${mapping.id || mapping.mappingId || `temp-${index}`}`}
                        checked={mapping.selected || false}
                        onChange={() => handleCheckboxChange('filter', mapping.id || mapping.mappingId)}
                      />
                      <label htmlFor={`filter-${mapping.id || mapping.mappingId || `temp-${index}`}`} className="custom-checkbox"></label>
                    </div>
                  </td>
                  <td>{mapping.displayOrder || index + 1}</td>
                  <td>{mapping.filterName || '-'}</td>
                  <td>필터</td>
                  <td><button className="info-btn" onClick={() => handleInfoClick('filter', mapping)}><i className="ri-information-line"></i></button></td>
                  <td><button className="edit-btn" onClick={() => handleEditClick('filter', mapping)}><i className="ri-pencil-line"></i></button></td>
                  <td><button className="delete-btn" onClick={() => handleDeleteClick('filter', mapping)}><i className="ri-close-line"></i></button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 테마 프레임 선택 모달 */}
      {showFrameModal && (
        <ThemeFrameSelectionModal
          themeId={themeId}
          availableFrames={availableFrames}
          onClose={handleCloseModal}
          onSave={(result) => handleSave('frame', result)}
          isEditMode={isEditMode}
          initialData={editTarget}
        />
      )}

      {/* 테마 필터 선택 모달 */}
      {showFilterModal && (
        <ThemeFilterSelectionModal
          themeId={themeId}
          availableFilters={availableFilters}
          onClose={handleCloseModal}
          onSave={(result) => handleSave('filter', result)}
          isEditMode={isEditMode}
          initialData={editTarget}
        />
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay active">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">{modalType === 'frame' ? '프레임' : '필터'} 삭제</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>이 {modalType === 'frame' ? '프레임' : '필터'}을(를) 삭제하시겠습니까?</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={handleCloseModal}
              >
                취소
              </button>
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleConfirmDelete}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세정보 모달 */}
      {showDetailModal && currentItem && (
        <div className="modal-overlay active" id={`${modalType}-detail-modal`}>
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">{modalType === 'frame' ? '프레임' : '필터'} 상세정보</h3>
              <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-item-container">
                <span className="detail-label">{modalType === 'frame' ? '프레임명' : '필터명'}</span>
                <span className="detail-content">{currentItem.name}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">타입</span>
                <span className="detail-content">{currentItem.type}</span>
              </div>
              {modalType === 'frame' && (
                <div className="detail-item-container">
                  <span className="detail-label">방향</span>
                  <span className="detail-content">{currentItem.direction}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleCloseDetailModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoSelectionModeSetting;